import React from 'react';
import { createRoot } from 'react-dom/client';
import { PlayerColumn } from './PlayerColumn';
// Importing the synced-session module's module-augmentation of `Window`
// so `window.hekateSyncedSession` is typed here.
import '../synced-session';
export function createPlayerColumnManager(props, deps) {
    let nextId = 1;
    const columns = [];
    // Synchronized autoplay across compare columns. Each column reports up
    // through `onMediaReady` once its own media has buffered. We block until
    // every media-bearing column has reported, then fire the queued play
    // callbacks together so neither runs ahead of the other on page load.
    // Non-media columns (image / document / text / commentstream / download)
    // never call onMediaReady and don't gate the start.
    const isMediaBearing = (col) => {
        const handle = col.handle.getPlayer();
        if (!handle)
            return true; // viewer not mounted yet — assume media until proven otherwise
        return handle.type === 'video' || handle.type === 'audio';
    };
    const tryRunSyncedAutoplay = () => {
        if (columns.length === 0)
            return;
        for (const col of columns) {
            if (isMediaBearing(col) && !col.mediaReady)
                return;
        }
        columns.forEach(col => {
            const play = col.pendingPlay;
            col.pendingPlay = null;
            if (play)
                play();
        });
    };
    const handleMediaReady = (colId, play) => {
        const col = columns.find(c => c.colId === colId);
        if (!col)
            return;
        col.mediaReady = true;
        col.pendingPlay = play ?? null;
        tryRunSyncedAutoplay();
    };
    let activeColId = null;
    const subscribers = new Set();
    let switchGuard;
    const snapshot = () => ({
        columns: columns.map(c => ({ colId: c.colId, fileId: c.fileId })),
        activeColId,
        activeFileId: columns.find(c => c.colId === activeColId)?.fileId ?? null,
    });
    // Subscribers must not call back into any manager mutation method during a
    // notify pass — reentrancy would deliver a stale snapshot to later subscribers.
    const notify = () => {
        const snap = snapshot();
        subscribers.forEach(cb => cb(snap));
    };
    const rerenderAll = (skipColId) => {
        const isOnly = columns.length === 1;
        columns.forEach(c => {
            if (c.colId === skipColId)
                return;
            c.handle.rerender({
                isActive: c.colId === activeColId,
                isOnly,
                fileId: c.fileId,
                refetchToken: c.refetchToken,
            });
        });
    };
    const openColumn = (fileId, initialData, makeActive = true, autoplay = true) => {
        // Synced Session guard: only one column is allowed while the session is
        // active. The caller (file browser "+" button, ad-hoc openColumn() in a
        // plugin, etc.) gets the existing active colId back so it doesn't crash
        // trying to dereference a freshly-opened one.
        if (props?.syncedSession?.enabled && columns.length >= 1) {
            console.warn('[PlayerColumnManager] openColumn blocked: Synced Session allows only one file at a time.');
            return activeColId ?? columns[0]?.colId ?? '';
        }
        const colId = `col-${nextId++}`;
        const hadActive = activeColId !== null;
        const willBeActive = makeActive || !hadActive;
        // Guard: if switching away from the current active viewer, check the guard
        if (willBeActive && hadActive && switchGuard && !switchGuard()) {
            // Abort — still open the column but don't make it active
            makeActive = false;
        }
        // Recompute willBeActive in case makeActive was just flipped by the guard
        const willBeActiveFinal = makeActive || !hadActive;
        // If the new column will become active, DEMOTE the existing active
        // column first — i.e. rerender it with `isActive: false` BEFORE we
        // mount the new column. This gives the previously active column's
        // viewer (HekatePlayer / HekateImageViewer) a chance to release any
        // singleton DOM resources it owns (e.g. the `#comment-tabs` side
        // panel) before the new column's mount effects try to claim them.
        // Without this ordering, two columns can race for ownership of the
        // same DOM node and crash React's reconciler.
        if (willBeActiveFinal && hadActive) {
            const prevActive = activeColId;
            activeColId = null;
            const isOnlyAfter = false; // we're about to add a second column
            columns.forEach(c => {
                if (c.colId === prevActive) {
                    c.handle.rerender({ isActive: false, isOnly: isOnlyAfter, fileId: c.fileId, refetchToken: c.refetchToken });
                }
            });
        }
        const handle = deps.mountColumn({
            colId, fileId,
            linkId: getLinkId(),
            initialData,
            isFolderLink: initialData?.link?.is_folder_link ?? false,
            linkName: initialData?.link?.link_name ?? null,
            linkRelativePath: initialData?.link?.link_relative_path ?? '/',
            isActive: willBeActiveFinal,
            isOnly: columns.length === 0,
            onSelect: (id) => setActive(id),
            onFileChange: (id, newId) => onFileChange(id, newId),
            onFileDataLoaded: (data) => {
                // When any column's file data loads or changes, notify
                // subscribers so legacy code (e.g. the comments version dropdown)
                // can re-derive from the new data.
                notify();
            },
            onViewerReady: (handle) => {
                // Notify subscribers when any column's viewer API becomes
                // available. This mirrors the onFileDataLoaded handler so legacy
                // code subscribed to manager changes can react to the player
                // becoming drivable.
                notify();
                // A non-media viewer becoming ready unblocks any other column's
                // pending synchronized autoplay (image columns don't call
                // onMediaReady themselves, so we re-evaluate here).
                if (handle.type !== 'video' && handle.type !== 'audio') {
                    tryRunSyncedAutoplay();
                }
            },
            onAddClick: () => onAddClick(),
            onCloseClick: (id) => onCloseClick(id),
            onAddVersionClick: (fileId) => {
                openColumn(fileId, undefined, false, false);
            },
            autoplay: autoplay,
            onWorkflowChange: (workflowId) => onWorkflowChange(fileId, workflowId),
            tabConfig: props?.tabConfig,
            syncedSession: props?.syncedSession,
            onMediaReady: (play) => handleMediaReady(colId, play),
        });
        columns.push({ colId, fileId, refetchToken: 0, mediaReady: false, pendingPlay: null, handle });
        if (willBeActiveFinal)
            activeColId = colId;
        // Skip re-rendering the column we just mounted — its initial render
        // already uses the correct props, and rerendering it would drop any
        // `initialData` that was passed in.
        rerenderAll(colId);
        notify();
        return colId;
    };
    const closeColumn = (colId) => {
        const idx = columns.findIndex(c => c.colId === colId);
        if (idx === -1)
            return;
        columns[idx].handle.unmount();
        columns.splice(idx, 1);
        if (activeColId === colId) {
            // After splice at idx: left neighbor is at idx-1 (unchanged), right neighbor is now at idx.
            // Prefer left, fall back to right, fall back to null.
            const neighbor = columns[idx - 1] ?? columns[idx] ?? null;
            activeColId = neighbor?.colId ?? null;
        }
        rerenderAll();
        // Closing a column may unblock pending autoplay on remaining columns.
        tryRunSyncedAutoplay();
        notify();
    };
    const setActive = (colId) => {
        if (activeColId === colId)
            return;
        if (!columns.some(c => c.colId === colId))
            return;
        // Guard: allow callers to abort the switch (e.g. unsaved annotations)
        if (switchGuard && !switchGuard())
            return;
        // Demote the previously-active column FIRST, so it releases any
        // singleton DOM resources (e.g. the `#comment-tabs` side panel)
        // before the newly-active column tries to adopt them. See the
        // matching comment in `openColumn` for the full rationale.
        const prevActive = activeColId;
        activeColId = null;
        const isOnly = columns.length === 1;
        if (prevActive) {
            columns.forEach(c => {
                if (c.colId === prevActive) {
                    c.handle.rerender({ isActive: false, isOnly, fileId: c.fileId, refetchToken: c.refetchToken });
                }
            });
        }
        activeColId = colId;
        rerenderAll();
        notify();
    };
    const onFileChange = (colId, newFileId) => {
        const col = columns.find(c => c.colId === colId);
        if (!col)
            return;
        col.fileId = newFileId;
        // New file means new media — reset readiness so synchronized autoplay
        // doesn't treat a stale canplay flag as ready for the new media.
        col.mediaReady = false;
        col.pendingPlay = null;
        // Leader broadcasts the new file id to followers so they can swap
        // their column in place (via replaceActiveColumn).
        if (props?.syncedSession?.enabled && props.syncedSession.role === 'leader') {
            const session = (typeof window !== 'undefined' ? window.hekateSyncedSession : undefined);
            session?.manager.sendFileChange(newFileId);
        }
        // Re-render so PlayerColumn's React tree sees the new fileId prop
        // and its useEffect refetches. Without this, version-pick from inside
        // a column would update the manager's state but leave the UI stale.
        rerenderAll();
        // Always notify when there are multiple columns so the URL compare
        // param and sync subscriptions update regardless of which column
        // changed. With a single column, only notify if it's the active one
        // (preserves the original behavior for the single-column case).
        if (activeColId === colId || columns.length > 1)
            notify();
    };
    const subscribe = (cb) => {
        subscribers.add(cb);
        return () => { subscribers.delete(cb); };
    };
    /**
     * Load a file into the active column. If there is no active column, opens a
     * new one for the file and returns its colId.
     */
    const loadFileIntoActiveColumn = (fileId) => {
        if (activeColId === null)
            return openColumn(fileId);
        const col = columns.find(c => c.colId === activeColId);
        if (!col)
            return activeColId;
        col.fileId = fileId;
        col.mediaReady = false;
        col.pendingPlay = null;
        rerenderAll();
        notify();
        return activeColId;
    };
    const replaceActiveColumn = (fileId) => {
        if (activeColId === null)
            return null;
        const col = columns.find(c => c.colId === activeColId);
        if (!col)
            return null;
        if (col.fileId === fileId)
            return activeColId;
        col.fileId = fileId;
        col.mediaReady = false;
        col.pendingPlay = null;
        // Mirrors loadFileIntoActiveColumn — rerender re-runs the fetch effect
        // inside PlayerColumn so file data refreshes without a mount/unmount cycle.
        rerenderAll();
        notify();
        return activeColId;
    };
    const reloadFile = (fileId) => {
        const col = columns.find(c => c.fileId === +fileId);
        if (!col)
            return;
        col.refetchToken = (col.refetchToken ?? 0) + 1;
        col.mediaReady = false;
        col.pendingPlay = null;
        col.handle.rerender({ isActive: col.colId === activeColId, isOnly: columns.length === 1, fileId, refetchToken: col.refetchToken });
        notify();
    };
    const reloadColumn = (colId) => {
        const col = columns.find(c => c.colId === colId);
        if (!col)
            return;
        col.refetchToken = (col.refetchToken ?? 0) + 1;
        col.mediaReady = false;
        col.pendingPlay = null;
        col.handle.rerender({ isActive: col.colId === activeColId, isOnly: columns.length === 1, fileId: col.fileId, refetchToken: col.refetchToken });
        notify();
    };
    const getActiveStageElement = () => {
        const col = columns.find(c => c.colId === activeColId);
        return col?.handle.getStageElement() ?? null;
    };
    const getActiveFileData = () => {
        const col = columns.find(c => c.colId === activeColId);
        return col?.handle.getFileData() ?? null;
    };
    const getColumnFileData = (colId) => {
        const col = columns.find(c => c.colId === colId);
        return col?.handle.getFileData() ?? null;
    };
    const getColumnPlayer = (colId) => {
        const col = columns.find(c => c.colId === colId);
        return col?.handle.getPlayer() ?? null;
    };
    const getActiveColumnPlayer = () => {
        if (activeColId === null)
            return null;
        return getColumnPlayer(activeColId);
    };
    const getColumnIdForFileId = (fileId) => {
        const col = columns.find(c => c.fileId === fileId);
        return col?.colId ?? null;
    };
    const getPlayerForFileId = (fileId) => {
        const col = columns.find(c => c.fileId === fileId);
        return col?.handle.getPlayer() ?? null;
    };
    const getActiveColumnElement = () => {
        const col = columns.find(c => c.colId === activeColId);
        return col?.handle.getElement() ?? null;
    };
    const getColumnElement = (colId) => {
        const col = columns.find(c => c.colId === colId);
        return col?.handle.getElement() ?? null;
    };
    const getColumnStageElement = (colId) => {
        const col = columns.find(c => c.colId === colId);
        return col?.handle.getStageElement() ?? null;
    };
    const onAddClick = () => {
        const w = window;
        if (typeof w.showFileBrowser !== 'function')
            return;
        w.showFileBrowser(-1, (itemID) => {
            // Pause the existing video
            const player = getActiveColumnPlayer();
            if (player && (player.type === 'video' || player.type === 'audio') && player.api) {
                player.api.pause();
            }
            // makeActive=false so the existing column keeps focus while a new
            // comparison column is opened to the right of it.
            openColumn(itemID, undefined, false, false);
        }, 'Choose a file to compare:', 'Compare Files');
    };
    const onCloseClick = (id) => {
        closeColumn(id);
    };
    const onWorkflowChange = async (fileId, workflowId) => {
        const params = new URLSearchParams();
        params.append('ids', fileId.toString());
        params.append('workflow', workflowId.toString());
        try {
            //      const response = await fetch(`/file/${fileId}/workflow`, {method: 'POST', body: params});
            const response = await fetch(`/ajax/file_ops`, { method: 'POST', body: params });
            const json = await response.json();
            if (json.success) {
                // Fetch new workflow data after delay to prevent race condition
                window.setTimeout(() => {
                    window.refreshApprovalPanel?.();
                }, 500);
            }
            else if (json.error) {
                console.error(json.error);
                alert(`Failed to change workflow: ${json.error}`);
            }
            else {
                console.log('Unknown error');
                alert('Failed to change workflow: Unknown error');
            }
        }
        catch (e) {
            console.error(e);
            alert('Failed to change workflow: Network error');
        }
    };
    const manager = {
        openColumn,
        closeColumn,
        setActive,
        getActiveColumnId: () => snapshot().activeColId,
        getActiveFileId: () => snapshot().activeFileId,
        getSnapshot: snapshot,
        subscribe,
        loadFileIntoActiveColumn,
        getActiveStageElement,
        getActiveFileData,
        getColumnFileData,
        getColumnPlayer,
        getActiveColumnPlayer,
        getPlayerForFileId,
        getColumnIdForFileId,
        getActiveColumnElement,
        getColumnElement,
        getColumnStageElement,
        reloadFile,
        reloadColumn,
        replaceActiveColumn,
        get syncedSession() { return props?.syncedSession; },
        get shouldSwitchActiveViewer() { return switchGuard; },
        set shouldSwitchActiveViewer(fn) { switchGuard = fn; },
    };
    return manager;
}
export function createDefaultMounter(container) {
    return function mountColumn(args) {
        const colDiv = document.createElement('div');
        colDiv.className = 'player-column';
        colDiv.dataset.colId = args.colId;
        container.appendChild(colDiv);
        const root = createRoot(colDiv);
        // `initialData` is intentionally NOT stored in `currentProps` —
        // it's only used on the first mount. `rerender` never forwards it,
        // so subsequent fileId changes (version switches) go through the
        // normal fetch path.
        // Closure-captured cache of the latest file data loaded by the
        // PlayerColumn React tree. Populated by the onFileDataLoaded callback
        // we pass into the props so getFileData() can return it synchronously.
        let latestFileData = args.initialData ?? null;
        let latestPlayerHandle = null;
        let currentProps = {
            colId: args.colId,
            fileId: args.fileId,
            linkId: args.linkId,
            isFolderLink: args.isFolderLink,
            initialData: args.initialData,
            isActive: args.isActive,
            isOnly: args.isOnly,
            // Initialize to 0 so the very first rerender (which carries the
            // column's manager-side refetchToken, also 0) is a no-op. Without
            // this, the prop transitions undefined → 0 on first rerender,
            // triggering PlayerColumn's fetch effect for no reason.
            refetchToken: 0,
            onSelect: args.onSelect,
            onFileChange: args.onFileChange,
            onFileDataLoaded: (data) => {
                latestFileData = data;
                args.onFileDataLoaded?.(data);
            },
            onViewerReady: (handle) => {
                latestPlayerHandle = handle;
                args.onViewerReady?.(handle);
            },
            onAddClick: args.onAddClick,
            onCloseClick: args.onCloseClick,
            onAddVersionClick: args.onAddVersionClick,
            autoplay: args.autoplay,
            onWorkflowChange: (workflowId) => args.onWorkflowChange?.(workflowId),
            tabConfig: args.tabConfig,
            syncedSession: args.syncedSession,
            onMediaReady: args.onMediaReady,
        };
        const syncAttrs = () => {
            colDiv.dataset.active = String(currentProps.isActive);
            colDiv.dataset.only = String(currentProps.isOnly);
            colDiv.dataset.fileId = String(currentProps.fileId);
        };
        const renderTree = () => {
            syncAttrs();
            root.render(React.createElement(PlayerColumn, currentProps));
        };
        renderTree();
        return {
            unmount: () => {
                root.unmount();
                colDiv.remove();
            },
            rerender: ({ isActive, isOnly, fileId, refetchToken }) => {
                // Drop initialData only when the fileId actually changes (e.g. version
                // switch), so the fetch effect runs. If fileId is unchanged, preserve
                // initialData — otherwise a rerender that fires before React 18's
                // concurrent root has committed the first render (e.g. compare mode
                // opens two columns back-to-back, and the second openColumn's
                // rerenderAll hits the first column before its initial commit) would
                // strip the data and cause PlayerColumn to mount with no initialData,
                // forcing an unnecessary GET /file/{id}.
                const fileIdChanged = fileId !== currentProps.fileId;
                currentProps = {
                    ...currentProps,
                    isActive,
                    isOnly,
                    fileId,
                    initialData: fileIdChanged ? undefined : currentProps.initialData,
                    // Preserve current refetchToken when caller doesn't pass one — bumping
                    // it back to 0 on every routine rerender (active change, file change,
                    // etc.) would re-trigger PlayerColumn's fetch effect any time the
                    // token had previously been incremented by reloadColumn/reloadFile.
                    refetchToken: refetchToken ?? currentProps.refetchToken ?? 0,
                };
                renderTree();
            },
            getStageElement: () => colDiv.querySelector('.player-stage'),
            getFileData: () => latestFileData,
            getPlayer: () => latestPlayerHandle,
            getElement: () => colDiv,
        };
    };
}
export function attachPlayerColumnManager(container, props = {}) {
    const manager = createPlayerColumnManager(props, {
        container,
        mountColumn: createDefaultMounter(container),
    });
    // Follower-side: when the leader broadcasts a file change, swap the
    // active column in place instead of reloading the page. Guarded on
    // `role === 'follower'` to avoid the leader echoing its own KFILE
    // broadcast back into its column manager.
    if (props.syncedSession?.enabled && props.syncedSession.role === 'follower') {
        const session = window.hekateSyncedSession;
        session?.manager.on('fileChange', (e) => {
            if (e.fileId > 0)
                manager.replaceActiveColumn(e.fileId);
        });
    }
    return manager;
}
function getLinkId() {
    const w = window;
    if (w.jsconnect?.link?.id)
        return String(w.jsconnect.link.id);
    return null;
}
if (typeof window !== 'undefined') {
    window.attachPlayerColumnManager = attachPlayerColumnManager;
}
